import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  Barcode,
  Check,
  Loader2,
  Receipt,
  Scale,
  ShoppingCart,
  Trash2,
  X,
} from 'lucide-react'
import clsx from 'clsx'

import { api, mensajeDeError } from '@/lib/api'
import { config } from '@/lib/config'
import { plataCentavos } from '@/lib/formato'
import { useSesion } from '@/lib/sesion'
import { confirmar } from '@/lib/confirmar'

import { leerEtiquetaBalanza } from './etiquetaBalanza'
import { traducirPromociones } from './promociones'
import { DialogoCantidad } from './DialogoCantidad'
import { DialogoCobro } from './DialogoCobro'
import { DialogoCobroDirecto } from './DialogoCobroDirecto'
import { SelectorCaja } from './SelectorCaja'
import { CajaSoloCobro } from './CajaSoloCobro'
import { DialogoTicket } from './DialogoTicket'
import { CajaCerrada } from './CajaCerrada'
import { GrillaProductos } from './GrillaProductos'
import { coincidencias, productosVisibles } from './busquedaProductos'
import { criterioDeOrden, ordenarProductos } from './ordenProductos'
import { moverMarca, type Direccion } from './navegacionGrilla'
import { Ticket } from './Ticket'
import { totalesDelTicket, useTicket } from './useTicket'
import { mensajeDeCobro } from './mensajeDeCobro'
import type {
  CobroDirecto,
  Cotizacion,
  EstadoTurno,
  LecturaBalanza,
  MedioPagoPos,
  OpcionesPos,
  ProductoPos,
  PromocionCruda,
  VentaRegistrada,
} from './tipos'

/**
 * La pantalla de venta, en la forma que le corresponde a este local.
 *
 * Son DOS pantallas y la elige un parámetro (§1.1, `pos.solo_cobro`): la de
 * siempre, con la grilla de productos, y la del local donde la balanza hace
 * toda la cuenta y lo único que hace falta es registrar cuánto entró.
 *
 * La decisión va ACÁ arriba, en un componente propio, y no con condicionales
 * adentro del POS. El motivo es concreto y no de prolijidad: el POS pide
 * catálogo, promociones y el peso de la balanza en cuanto se monta, así que
 * esconder la grilla con un `if` dejaría esas consultas corriendo igual en un
 * local que no tiene catálogo —y el corte por catálogo viejo, que bloquea la
 * venta, mirando algo que ahí no significa nada—.
 *
 * Mientras no se sabe no se pinta ninguna de las dos: arrancar con la del
 * catálogo y cambiar medio segundo después le mostraría la grilla de productos
 * a un local que justamente no la quiere ver. La consulta está cacheada cinco
 * minutos y en la caja del mostrador es local, así que el cartel se ve una vez
 * por sesión.
 *
 * Si la consulta FALLA se cae a la pantalla de siempre: es la que sirve para
 * los dos casos —tiene el cobro sin detalle en F3— y quedarse sin poder cobrar
 * porque no se pudo leer un parámetro sería la peor respuesta posible.
 */
export function Pos() {
  const opciones = useQuery({
    queryKey: ['pos', 'opciones'],
    queryFn: () => api.get<OpcionesPos>('/pos/opciones'),
    retry: false,
    staleTime: 5 * 60_000,
  })

  if (opciones.isLoading) {
    return (
      <div className="grid h-full place-items-center">
        <Loader2 className="h-5 w-5 animate-spin text-verde" />
      </div>
    )
  }

  return opciones.data?.soloCobro === true ? <CajaSoloCobro /> : <PosConProductos />
}

// Pantalla de venta con la grilla de productos.
//
// Es la pantalla que se mira ocho horas por día, así que las decisiones de
// acá no son de estética:
//
//   - El foco vive en el campo de código. Un lector de códigos de barras es
//     un teclado: escribe los dígitos y manda Enter. Si el foco está en otro
//     lado, escanear no hace nada y nadie entiende por qué.
//   - El total va abajo a la derecha, grande. Es lo que el cajero y el
//     cliente miran, y es lo último que se lee antes de cobrar.
//   - Nada bloquea la carga de líneas. El estado de la balanza, la conexión y
//     el turno se muestran, pero no interrumpen: el sistema tiene que
//     desaparecer mientras hay gente esperando.
function PosConProductos() {
  const qc = useQueryClient()
  const { usuario, puede } = useSesion()

  const capacidades = config().capacidades

  const {
    lineas,
    pagos,
    entregadoCentavos,
    cliente,
    seleccionada,
    fijarPromociones,
    agregar,
    cambiarCantidad,
    sumarUnidad,
    quitar,
    seleccionar,
    agregarPago,
    quitarPago,
    fijarEntregado,
    fijarCliente,
    limpiar,
    ulid,
  } = useTicket()

  const totales = totalesDelTicket(useTicket())

  const [codigo, setCodigoCrudo] = useState('')
  const [categoria, setCategoriaCruda] = useState<number | null>(null)

  // Qué producto de la grilla marcaron las flechas. `null` es el estado
  // normal: mientras se tipea no hay ninguno elegido y el Enter hace lo de
  // siempre. Ver `navegacionGrilla`.
  const [marcado, setMarcado] = useState<number | null>(null)

  // Cuántas columnas entran, para que ↓ baje una FILA y no un producto. Lo
  // informa la grilla, que es la que sabe cuántas dibujó.
  const [columnas, setColumnas] = useState(1)

  const [aviso, setAviso] = useState<string | null>(null)
  const [pidiendoCantidad, setPidiendoCantidad] = useState<{
    producto: ProductoPos
    uid?: string
    inicial?: number
  } | null>(null)
  const [cobrando, setCobrando] = useState(false)
  const [cobroDirecto, setCobroDirecto] = useState(false)
  const [cotizacion, setCotizacion] = useState<Cotizacion | null>(null)
  const [cotizando, setCotizando] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [errorCobro, setErrorCobro] = useState<string | null>(null)
  const [emitida, setEmitida] = useState<VentaRegistrada | null>(null)

  // La venta que se acaba de registrar cuando el local NO quiere el ticket en
  // pantalla. Se muestra como un renglón en el panel del ticket en lugar de un
  // diálogo, y con el VUELTO a la vista: sin eso, apagar el ticket dejaría al
  // cajero sin saber cuánto tiene que devolver, que es lo único de esa
  // pantalla que no se puede consultar después.
  const [ultima, setUltima] = useState<VentaRegistrada | null>(null)

  const refCodigo = useRef<HTMLInputElement>(null)

  // Tocar el texto o el chip de categoría BORRA la marca, y no es prolijidad:
  // la marca es una posición de la lista que se está mostrando, así que si la
  // lista cambia y el número se queda, el Enter agrega un producto distinto
  // del que está resaltado. Es el mismo cuidado que hizo que `coincidencias`
  // saliera del componente.
  const setCodigo = useCallback((v: string) => {
    setCodigoCrudo(v)
    setMarcado(null)
  }, [])

  const setCategoria = useCallback((id: number | null) => {
    setCategoriaCruda(id)
    setMarcado(null)
  }, [])

  // ── Datos ──

  const productos = useQuery({
    queryKey: ['pos', 'productos'],
    queryFn: () => api.get<ProductoPos[]>('/productos/pos'),

    // Un minuto de caché evita releerlo en cada venta.
    staleTime: 60_000,

    // Y se vuelve a pedir solo, porque el catálogo SÍ cambia mientras el
    // cajero atiende: cambiarle el precio al tomate desde la oficina es una
    // de las cosas que más se hacen en el día. Sin esto, esta consulta se
    // pedía UNA vez —la pantalla queda montada todo el día y en la caja el
    // refresco al volver a la pestaña está apagado a propósito—, así que la
    // grilla mostraba el precio de la mañana hasta que alguien cambiara de
    // pantalla, mientras el servidor ya cotizaba el nuevo: el cajero canta un
    // número y la caja cobra otro.
    //
    // En el ejecutable del mostrador esto es la RED de seguridad y no el
    // camino principal: ahí el chip de sincronización avisa en cuanto entra
    // algo nuevo y la grilla se actualiza en segundos (ver
    // EstadoSincronizacion). En el navegador no hay quien avise, así que lo
    // que queda es preguntar cada tanto.
    refetchInterval: 3 * 60_000,
  })

  const medios = useQuery({
    queryKey: ['pos', 'medios-pago'],
    queryFn: () => api.get<MedioPagoPos[]>('/medios-pago'),
    staleTime: 5 * 60_000,
  })

  // Qué habilitó el local. Se pide a la API y no a /config.json porque es del
  // LOCAL y no de la máquina: el dueño lo prende una vez desde Gestión y lo
  // ven las dos cajas y la web. Que este pedido falle no puede impedir vender:
  // sin respuesta, el botón simplemente no aparece.
  const opciones = useQuery({
    queryKey: ['pos', 'opciones'],
    queryFn: () => api.get<OpcionesPos>('/pos/opciones'),
    retry: false,
    staleTime: 5 * 60_000,
  })

  const turno = useQuery({
    queryKey: ['pos', 'turno'],
    queryFn: () => api.get<EstadoTurno>('/turno'),
    refetchInterval: 30_000,
  })

  const promociones = useQuery({
    queryKey: ['pos', 'promociones'],
    queryFn: () => api.get<PromocionCruda[]>('/promociones'),

    // Que no haya promociones (o que este host todavía no las sirva) no puede
    // impedir vender: se sigue con el precio de lista y el servidor tiene la
    // última palabra en la cotización.
    retry: false,
    staleTime: 60_000,

    // Por lo mismo que el catálogo, y acá todavía más: una promo se carga
    // para que empiece HOY, y una que la pantalla no conoce es un descuento
    // que el cartel promete y el renglón no muestra.
    refetchInterval: 3 * 60_000,
  })

  // La balanza se sondea a DOS velocidades.
  //
  // Rápido (cada 350 ms) solo cuando hay un diálogo de cantidad abierto: ahí
  // el peso se muestra en vivo y tiene que seguir a la mercadería que se
  // apoya. El resto del tiempo, cada tres segundos, que alcanza de sobra para
  // el cartelito de «conectada / sin balanza» de la barra.
  //
  // Sondear rápido siempre serían tres requests por segundo durante ocho
  // horas para pintar un cartel. Y no sondear nunca dejaba el cartel diciendo
  // «sin balanza» aunque estuviera enchufada, que es peor: el cajero deja de
  // confiar en el cartel.
  const balanza = useQuery({
    queryKey: ['pos', 'balanza'],
    queryFn: () => api.get<LecturaBalanza>('/balanza/peso'),
    enabled: capacidades.balanza,
    refetchInterval: pidiendoCantidad !== null ? 350 : 3000,
    retry: false,
  })

  useEffect(() => {
    if (promociones.data) fijarPromociones(traducirPromociones(promociones.data))
  }, [promociones.data, fijarPromociones])

  // ── Foco para el lector de códigos ──

  const volverElFoco = useCallback(() => {
    // Se devuelve el foco al campo de código en cuanto no hay un diálogo
    // abierto: es lo que hace que escanear funcione sin tocar nada.
    if (!pidiendoCantidad && !cobrando && !emitida) refCodigo.current?.focus()
  }, [pidiendoCantidad, cobrando, emitida])

  useEffect(volverElFoco, [volverElFoco])

  // Y un clic con el mouse se lo lleva, así que se lo devuelve el mismo clic.
  //
  // Un <button> que se toca queda ENFOCADO, y desde ahí el Enter es de ese
  // botón: lo activa el navegador por su cuenta, antes que cualquier atajo. Con
  // eso, tocar el «+» de un renglón —o una tarjeta de la grilla— y después
  // elegir otro producto con las flechas terminaba en un Enter que volvía a
  // apretar el botón de antes en lugar de agregar lo marcado. Y la pantalla lo
  // mostraba: dos productos resaltados, el que se había tocado y el que
  // marcaron las flechas, sin nada que dijera cuál de los dos iba a entrar.
  //
  // Va UNA sola vez y acá arriba, no botón por botón: escrito en cada
  // `onClick`, el botón que se agregue mañana nace con el bug. Es la misma
  // familia que `soltarElFoco` del cobro, pagada por segunda vez, y acá el foco
  // se DEVUELVE en lugar de soltarse porque en el POS tiene un lugar donde
  // vivir —el buscador, que es donde escribe el lector de códigos—.
  //
  // Solo el clic de VERDAD (`detail > 0`): al que llegó con Tab hasta un chip,
  // el Enter le tiene que apretar el chip, que es lo que espera, y esa
  // activación llega como un click con `detail` en cero.
  //
  // Y va en la CAPTURA, que es lo que lo hace valer para todos: los botones
  // del renglón del ticket cortan la propagación —tienen que hacerlo, o tocar
  // el «+» además seleccionaría el renglón— así que un `onClick` normal no se
  // enteraba justo del botón que el local reportó primero. Bajando, nadie lo
  // puede frenar, y el foco se devuelve ANTES de que el botón haga lo suyo,
  // que da igual: el clic ya está despachado.
  const alTocar = (e: React.MouseEvent) => {
    if (e.detail === 0) return

    // Con un diálogo abierto el foco es de él, y se pregunta por `dialog[open]`
    // y no por los estados de esta pantalla, por lo mismo que el atajo de
    // Escape: la lista de diálogos se queda corta con cada uno que se agrega.
    if (document.querySelector('dialog[open]') !== null) return

    volverElFoco()
  }

  // ── Carga de líneas ──

  // El `?? []` va DENTRO de un useMemo y no suelto: un array literal nuevo en
  // cada render invalida los dos mapas de abajo, que se rearman con todo el
  // catálogo. En la caja, con la balanza sondeada cada 350 ms, eso es rearmar
  // dos mapas de trescientas entradas tres veces por segundo.
  // El catálogo YA ORDENADO con el criterio que eligió el local. Se ordena una
  // sola vez y acá arriba, no en la grilla: los chips, las flechas y el Enter
  // tienen que ver exactamente la misma lista, que es la regla que ya vale
  // para el filtro (ver `productosVisibles`).
  const catalogo = useMemo(
    () => ordenarProductos(productos.data ?? [], criterioDeOrden(opciones.data?.ordenProductos)),
    [productos.data, opciones.data?.ordenProductos],
  )

  const porCodigo = useMemo(() => {
    const mapa = new Map<string, ProductoPos>()

    for (const p of catalogo) {
      if (p.codigoCorto) mapa.set(p.codigoCorto, p)
      if (p.codigoBarras) mapa.set(p.codigoBarras, p)
    }

    return mapa
  }, [catalogo])

  // Lo que la grilla está mostrando. Se calcula ACÁ y se lo pasa a la grilla
  // ya filtrado: las flechas marcan una posición de esta lista y el Enter
  // agrega de esta lista, así que no puede haber dos.
  const visibles = useMemo(
    () => productosVisibles(catalogo, codigo, categoria),
    [catalogo, codigo, categoria],
  )

  const porPlu = useMemo(() => {
    const mapa = new Map<number, ProductoPos>()

    for (const p of catalogo) if (p.pluBalanza) mapa.set(p.pluBalanza, p)

    return mapa
  }, [catalogo])

  const elegir = (p: ProductoPos, codigoBalanza?: string, cantidad?: number) => {
    setAviso(null)

    // Lo pesable pide la cantidad; lo que se vende por unidad entra directo
    // con una. Preguntar la cantidad de una lechuga es un diálogo de más en
    // cada venta.
    if (cantidad !== undefined) {
      agregar(p, { cantidadMilesimas: cantidad, codigoBalanzaLeido: codigoBalanza })
      return
    }

    if (p.pesable) setPidiendoCantidad({ producto: p })
    else agregar(p)
  }

  // El Enter del ÚNICO buscador de la pantalla.
  //
  // Por el mismo campo entran cuatro cosas: lo que dispara el lector, la
  // etiqueta que imprime la balanza, un PLU tipeado y el nombre del producto
  // escrito a mano. Antes eran dos campos, uno al lado del otro, y el cajero
  // tenía que decidir en cuál tipear antes de empezar —y el lector escribe en
  // el que tenga el foco, así que escanear con el foco en el otro dejaba el
  // código pegado en un filtro—.
  //
  // El texto se limpia solo cuando se RESOLVIÓ. Si quedaron varias
  // coincidencias no se toca: la grilla las está mostrando y borrarlas sería
  // deshacer la búsqueda en el momento en que sirve.
  const procesarCodigo = () => {
    // 0. Si el cajero marcó un producto con las flechas, ese es el que entra,
    //    y antes que nada: ya eligió. Sin esto, «papa» con cinco coincidencias
    //    seguiría sin resolver nada aunque haya una resaltada en pantalla.
    if (marcado !== null) {
      const elegido = visibles[marcado]

      if (elegido) {
        setCodigo('')
        elegir(elegido)
        return
      }
    }

    const texto = codigo.trim()
    if (!texto) return

    // 1. Etiqueta de balanza: un EAN-13 que embebe el PLU y el peso o el
    //    importe. Es el camino principal del primer cliente (una Systel
    //    Cuora, que es etiquetadora), así que se prueba primero.
    const etiqueta = leerEtiquetaBalanza(texto)

    if (etiqueta) {
      const producto = porPlu.get(etiqueta.plu)

      if (!producto) {
        setAviso(`La etiqueta trae el PLU ${etiqueta.plu} y ningún producto lo tiene asignado.`)
        return
      }

      if (etiqueta.esImporte) {
        agregar(producto, {
          importeObjetivoCentavos: etiqueta.valor,
          codigoBalanzaLeido: texto,
        })
      } else {
        agregar(producto, { cantidadMilesimas: etiqueta.valor, codigoBalanzaLeido: texto })
      }

      setCodigo('')
      return
    }

    // 2. Código de barras o código corto del catálogo.
    const producto = porCodigo.get(texto)

    if (producto) {
      setCodigo('')
      elegir(producto)
      return
    }

    // 3. Es un nombre tipeado a mano. Se usa la MISMA función que filtra la
    //    grilla, así que lo que agrega el Enter es exactamente lo único que
    //    hay en pantalla: escrita dos veces, el Enter podría agregar un
    //    producto distinto del que el cajero está viendo.
    const candidatos = coincidencias(catalogo, texto)

    if (candidatos.length === 1) {
      setCodigo('')
      elegir(candidatos[0]!)
      return
    }

    // Varios: la grilla ya los muestra, así que el Enter no tiene nada que
    // decidir y el texto queda para seguir afinándolo.
    if (candidatos.length > 1) return

    // Ninguno. Acá SÍ se limpia, aunque no se haya resuelto nada: lo más
    // probable es que haya venido del lector, y dejarlo puesto haría que el
    // próximo escaneo se pegue atrás del anterior.
    setCodigo('')
    setAviso(`No hay ningún producto con el código o el nombre «${texto}».`)
  }

  // ── Cobro ──

  /** Cuerpo de la venta, tal como lo espera el host. */
  const cuerpoDeLaVenta = (ulid?: string) => ({
    ulid: ulid ?? '',

    lineas: lineas.map((l) => ({
      productoIdNube: l.producto.id,
      cantidadMilesimas: l.importeObjetivoCentavos ? 0 : l.cantidadMilesimas,
      importeObjetivoCentavos: l.importeObjetivoCentavos,
      descuentoCentavos: l.descuentoCentavos,
      codigoBalanzaLeido: l.codigoBalanzaLeido,
    })),

    // Los pagos se leen del STORE y no de la variable de este render.
    //
    // El diálogo de cobro materializa el pago implícito —el de un solo medio,
    // que no hace falta «agregar»— justo antes de llamar a cobrar, así que
    // entre el `agregarPago` y el `cuerpoDeLaVenta` no hay un render de por
    // medio: con la copia del render, la venta viajaría SIN pagos y el
    // servidor la rechazaría con el cliente adelante. El store de zustand se
    // actualiza en el acto, así que acá se lee lo que de verdad hay.
    pagos: useTicket.getState().pagos.map((p) => ({
      medioPagoIdNube: p.medio.id,
      importeCentavos: p.importeCentavos,
      referencia: p.referencia,
    })),

    entregadoCentavos: entregadoCentavos ?? undefined,

    // El cliente viaja con el mismo nombre en los dos hosts: la nube lo llama
    // clienteId y la caja clienteIdNube, así que se mandan los dos. El host
    // que no conoce uno de los dos lo ignora, y así el SPA sigue siendo uno.
    clienteId: cliente?.id,
    clienteIdNube: cliente?.id,

    imprimir: capacidades.impresora,
  })

  /**
   * Abre el cobro con el total que dice EL SERVIDOR.
   *
   * El navegador ya calculó un total con el mismo motor, pero el que manda es
   * el del servidor: si difirieran (un catálogo desfasado, una promoción que
   * el bundle no tenía), el cobro se rechazaría con el cliente adelante.
   * Preguntar cuesta un request contra 127.0.0.1.
   */
  const abrirCobro = async () => {
    if (lineas.length === 0) return

    setCotizando(true)
    setErrorCobro(null)

    try {
      const c = await api.post<Cotizacion>('/ventas/cotizar', cuerpoDeLaVenta())

      setCotizacion(c)
      setCobrando(true)
    } catch (e) {
      setAviso(
        mensajeDeError(e, 'No se pudo calcular el total. Revisá que el sistema esté corriendo.'),
      )
    } finally {
      setCotizando(false)
    }
  }

  /**
   * Qué se ve después de registrar: el ticket, o un renglón que lo resume.
   *
   * Lo decide el LOCAL (§1.1, parámetro `pos.mostrar_ticket`). El diálogo es lo
   * correcto donde el papel sale de ahí; donde no se entrega ticket —o lo
   * imprime otro equipo— es una pantalla que hay que cerrar entre dos clientes,
   * o sea dos toques por venta durante todo el día. Está en una sola función
   * porque las dos formas de cobrar tienen que hacer lo mismo.
   *
   * Ausente cuenta como SÍ: el parámetro se agregó después, y el que hoy
   * imprime desde acá no se puede quedar sin el papel por una fila que todavía
   * no bajó a su caja.
   */
  const mostrarLoRegistrado = (venta: VentaRegistrada) => {
    if (opciones.data?.mostrarTicket === false) setUltima(venta)
    else setEmitida(venta)
  }

  const cobrar = async () => {
    if (lineas.length === 0 || totales.total <= 0) return

    setEnviando(true)
    setErrorCobro(null)
    setUltima(null)

    try {
      // El ULID sale DEL TICKET y no se genera acá (ver useTicket): es la
      // clave de idempotencia, y generarla al mandar la hacía inútil justo en
      // el caso para el que existe. Si el pedido se corta después de que el
      // servidor guardó, el cajero vuelve a apretar Cobrar, va el mismo id y
      // vuelve la misma venta en lugar de una segunda.
      const venta = await api.post<VentaRegistrada>('/ventas', cuerpoDeLaVenta(ulid))

      mostrarLoRegistrado(venta)
      setCobrando(false)
      setCotizacion(null)
      limpiar()

      // El turno cambió (una venta más), así que se refresca el arqueo.
      void qc.invalidateQueries({ queryKey: ['pos', 'turno'] })
    } catch (e) {
      setErrorCobro(mensajeDeCobro(e, 'No se pudo registrar la venta. Reintentá.'))
    } finally {
      setEnviando(false)
    }
  }

  /**
   * Registra un cobro SIN DETALLE.
   *
   * Va por el MISMO endpoint que la venta normal y no por uno propio: es una
   * venta —suma al arqueo, al turno, al talonario y al reporte de medios de
   * pago—, lo único que no tiene es el detalle de la mercadería. Con un
   * endpoint aparte habría que duplicar el turno, la numeración, el ticket y la
   * idempotencia, y el día que alguno de los dos cambiara, la plata de esta
   * operatoria se contaría distinto.
   *
   * El ULID lo trae el diálogo, estable desde que se abrió: es la clave de
   * idempotencia y por eso no se genera acá.
   */
  const registrarCobroDirecto = async (datos: CobroDirecto) => {
    setEnviando(true)
    setErrorCobro(null)
    setUltima(null)

    try {
      const venta = await api.post<VentaRegistrada>('/ventas', {
        ...datos,
        esCobroDirecto: true,
        lineas: [],
        imprimir: capacidades.impresora,
      })

      mostrarLoRegistrado(venta)
      setCobroDirecto(false)

      void qc.invalidateQueries({ queryKey: ['pos', 'turno'] })
    } catch (e) {
      setErrorCobro(mensajeDeCobro(e, 'No se pudo registrar el cobro. Reintentá.'))
    } finally {
      setEnviando(false)
    }
  }

  const reimprimir = async () => {
    if (!emitida) return

    try {
      await api.post('/impresora/ticket', { ulid: emitida.ulid })
    } catch {
      // Si no salió el papel no hay nada que hacer desde acá; la venta está
      // guardada igual y el ticket sigue en pantalla.
    }
  }

  // El turno y las opciones se leen ACÁ y no en el render porque los atajos de
  // teclado también los miran: F3 no puede abrir un cobro en una caja cerrada.
  const sinTurno = turno.data?.abierto === false

  // Doble puerta, y las dos hacen falta: el parámetro dice si el LOCAL usa
  // esta operatoria y el permiso dice quién. Sin el parámetro, el botón
  // aparecería en los locales que cargan la mercadería y el reporte de qué se
  // vende se vaciaría solo.
  const puedeCobroDirecto =
    opciones.data?.cobroDirecto === true && puede('venta.cobro_directo') && !sinTurno

  // ── Atajos ──

  // El renglón sobre el que actúan los atajos del ticket: el que está
  // seleccionado, y si no, el ÚLTIMO que entró. Esa segunda mitad es la que
  // hace que sirvan sin tocar nada: el error que se corrige en el mostrador es
  // el del renglón que se acaba de cargar —pesó de más, entraron dos— y
  // `agregar` ya deja seleccionado el nuevo.
  const renglonActivo = () => seleccionada ?? lineas.at(-1)?.uid ?? null

  useEffect(() => {
    const alTeclado = (e: KeyboardEvent) => {
      // Con un diálogo abierto las teclas son de él: tiene su propio teclado
      // numérico, su Enter y su Escape. Se pregunta por `dialog[open]` y no
      // por los estados de esta pantalla, por lo mismo que el Escape de más
      // abajo: la lista de diálogos se queda corta con cada uno que se agrega,
      // y el cajón de ayuda nunca estuvo en ella.
      if (document.querySelector('dialog[open]') !== null) return

      // F2 cobra. Es el atajo que reemplaza al mouse en una caja con teclado.
      if (e.key === 'F2' && lineas.length > 0 && !cobrando && !emitida) {
        e.preventDefault()
        void abrirCobro()
      }

      // F3 abre el cobro sin detalle, cuando el local lo tiene habilitado.
      // Es el atajo pegado al de cobrar porque en ese local es LA operación
      // del día, no una excepción.
      if (e.key === 'F3' && puedeCobroDirecto && !cobrando && !cobroDirecto && !emitida) {
        e.preventDefault()
        setCobroDirecto(true)
      }

      // Las flechas recorren la grilla y Enter agrega lo marcado.
      //
      // Es lo que faltaba para no tocar el mouse: filtrar dejaba cinco
      // manzanas en pantalla y el Enter —que resuelve solo cuando queda UNA—
      // no tenía nada que decidir, así que elegir entre esas cinco era
      // exclusivamente con el dedo.
      //
      // Las cuatro flechas se toman ACÁ y no en el input a propósito: el foco
      // vive en el campo de búsqueda (el lector de códigos es un teclado) y
      // tiene que seguir ahí mientras se navega. La contrapartida es que ←/→
      // dejan de mover el cursor del texto MIENTRAS hay algo marcado, y es a
      // sabiendas: con la marca puesta el cajero está eligiendo un producto,
      // no corrigiendo lo que tipeó. Sin marca, el campo es un campo.
      const flechas: Record<string, Direccion> = {
        ArrowDown: 'abajo',
        ArrowUp: 'arriba',
        ArrowLeft: 'izquierda',
        ArrowRight: 'derecha',
      }

      const direccion = flechas[e.key]

      if (direccion && !e.altKey) {
        // ←/→ con el campo escrito y nada marcado son del cursor del texto.
        const horizontal = direccion === 'izquierda' || direccion === 'derecha'
        if (horizontal && marcado === null) return

        if (visibles.length === 0) return

        e.preventDefault()
        setMarcado((m) => moverMarca(m, direccion, visibles.length, columnas))
        return
      }

      // Alt + ↑/↓ recorre los RENGLONES del ticket. Va con Alt porque las
      // flechas solas son de la grilla, que es lo que se usa todo el día; esto
      // es para volver a un renglón anterior, que pasa una vez cada tanto.
      if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && e.altKey && lineas.length > 0) {
        e.preventDefault()

        const actual = lineas.findIndex((l) => l.uid === seleccionada)
        const paso = e.key === 'ArrowDown' ? 1 : -1
        const base = actual === -1 ? (paso === 1 ? -1 : lineas.length) : actual
        const destino = Math.min(Math.max(base + paso, 0), lineas.length - 1)

        seleccionar(lineas[destino]!.uid)
        return
      }

      // F4 corrige la cantidad del renglón activo. Es EL error del mostrador
      // —se pesó de más, entraron dos en lugar de una— y hasta ahora se
      // arreglaba tocando la cantidad subrayada del renglón, o sea con el
      // mouse.
      if (e.key === 'F4' && !cobrando && !emitida) {
        const uid = renglonActivo()
        const l = uid ? lineas.find((x) => x.uid === uid) : null

        if (l) {
          e.preventDefault()
          setPidiendoCantidad({ producto: l.producto, uid: l.uid, inicial: l.cantidadMilesimas })
        }

        return
      }

      // Y lo que se hace con el renglón activo: sumar, restar y quitar.
      //
      // Estas tres piden el buscador VACÍO, que es su estado normal entre
      // renglón y renglón —el texto se limpia solo al resolver—. Es lo que
      // evita el choque: con algo tipeado, «-» es parte de lo que se escribe y
      // Supr borra el caracter de adelante, como en cualquier campo. Un atajo
      // que se come una tecla mientras se busca un producto es peor que no
      // tenerlo.
      const buscadorVacio = codigo === ''
      const uidActivo = renglonActivo()

      if (buscadorVacio && uidActivo && !cobrando && !emitida) {
        if (e.key === '+') {
          e.preventDefault()
          sumarUnidad(uidActivo, 1)
          return
        }

        if (e.key === '-') {
          e.preventDefault()
          sumarUnidad(uidActivo, -1)
          return
        }

        if (e.key === 'Delete') {
          e.preventDefault()
          quitar(uidActivo)
          return
        }
      }

      // Escape devuelve el foco al campo de código, y SIN preventDefault.
      //
      // El preventDefault que estaba acá rompía el Escape de todos los
      // diálogos: la tecla Escape en un <dialog> modal cierra el diálogo como
      // ACCIÓN POR DEFECTO del navegador, así que prevenirla lo deja abierto.
      // El síntoma era el cajón de ayuda: se abría con F1 y no se cerraba con
      // Escape en ninguna pantalla del POS, y el que lo abría se quedaba con la
      // ayuda tapando la venta.
      //
      // Y se mira si hay ALGÚN <dialog open> en lugar de enumerar los diálogos
      // de esta pantalla: la lista se quedaba corta cada vez que se agregaba
      // uno (pasó con el cobro sin detalle), y el cajón de ayuda nunca estuvo
      // en ella porque no es de esta pantalla.
      if (e.key === 'Escape') {
        // Con algo marcado, Escape sale de la grilla y deja el texto como
        // estaba: es la salida de «me equivoqué de producto» sin perder la
        // búsqueda.
        if (marcado !== null) setMarcado(null)

        volverElFoco()
        return
      }

      // Y CUALQUIER tecla que escribe va al buscador, esté donde esté el foco.
      //
      // El foco vive en el campo de búsqueda casi siempre, pero se va en
      // cuanto alguien toca un chip de categoría, una tarjeta o un botón del
      // ticket con el dedo o con el mouse —que es lo normal en el mostrador—,
      // y desde ahí tipear no hacía nada: había que volver al campo con el
      // mouse para poder buscar. Con esto se sigue tipeando y la grilla
      // filtra, sin importar dónde quedó el foco ni si había un producto
      // marcado con las flechas (tipear borra la marca, que es la regla que ya
      // estaba: la marca es una posición de la lista que se está mostrando).
      const activo = document.activeElement

      // Si el foco YA está en un campo, el campo se encarga: meter mano acá
      // duplicaría cada letra.
      if (
        activo instanceof HTMLInputElement ||
        activo instanceof HTMLTextAreaElement ||
        (activo instanceof HTMLElement && activo.isContentEditable)
      ) {
        return
      }

      // La barra espaciadora sobre un botón lo ACTIVA, y eso es del navegador:
      // quien llegó con Tab hasta un chip espera que el espacio lo apriete. El
      // resto de las teclas no activan nada, así que se pueden desviar.
      const enUnBoton =
        activo instanceof HTMLButtonElement || activo instanceof HTMLAnchorElement

      if (e.key === ' ' && enUnBoton) return

      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault()
        setCodigo(codigo + e.key)
        volverElFoco()
        return
      }

      // Y borrar también, que es la otra mitad: el que se equivoca tipeando
      // corrige con Retroceso sin volver al campo. Lleva preventDefault
      // siempre —aunque no haya nada que borrar— porque sin foco en un campo,
      // Retroceso es «volver atrás» en algunos navegadores, y volver atrás
      // desde el POS saca al cajero de la pantalla de venta.
      if (e.key === 'Backspace') {
        e.preventDefault()
        if (codigo !== '') setCodigo(codigo.slice(0, -1))
        volverElFoco()
      }
    }

    window.addEventListener('keydown', alTeclado)
    return () => window.removeEventListener('keydown', alTeclado)

    // abrirCobro se recrea en cada render y no se agrega a las dependencias a
    // propósito: agregarlo re-suscribiría el listener del teclado en cada
    // tecla que se toca, y lo que la función necesita (las líneas, los pagos)
    // lo lee del store en el momento de correr.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lineas,
    seleccionada,
    seleccionar,
    sumarUnidad,
    quitar,
    codigo,
    marcado,
    visibles.length,
    columnas,
    cobrando,
    cobroDirecto,
    puedeCobroDirecto,
    emitida,
    pidiendoCantidad,
    volverElFoco,
    setCodigo,
  ])

  // ── Render ──

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-3 lg:flex-row" onClickCapture={alTocar}>
      {/* Izquierda: catálogo */}
      <section className="flex min-h-0 flex-1 flex-col gap-3">
        <BarraSuperior
          codigo={codigo}
          setCodigo={setCodigo}
          onEnter={procesarCodigo}
          refCodigo={refCodigo}
          conBalanza={capacidades.balanza}
          balanzaConectada={balanza.data?.conectada ?? false}
        />

        {aviso && (
          <p className="flex items-start gap-2 rounded-lg bg-ambar/10 px-3 py-2.5 text-[13px] text-ambar">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {aviso}
          </p>
        )}

        {/* El cartel de las flechas aparece SOLO cuando sirve: con una
            búsqueda que dejó varios productos, que es el momento exacto en el
            que antes había que ir al mouse. Puesto siempre sería una línea de
            ruido permanente en la pantalla que se mira ocho horas; puesto
            nunca, es la tecla rápida del medio de pago otra vez —el mecanismo
            existe y nadie se entera—. */}
        {codigo.trim() !== '' && visibles.length > 1 && (
          <p className="hidden text-[12.5px] text-tinta-tenue sm:block">
            <span className="num">↑ ↓ ← →</span> para elegir y{' '}
            <span className="num">Enter</span> para agregar el que quede marcado.
          </p>
        )}

        {productos.isPending ? (
          <p className="py-16 text-center text-[14px] text-tinta-tenue">Cargando el catálogo…</p>
        ) : productos.isError ? (
          <p className="py-16 text-center text-[14px] text-rojo">
            No se pudo leer el catálogo. Revisá que el sistema esté corriendo.
          </p>
        ) : (
          <GrillaProductos
            productos={catalogo}
            visibles={visibles}
            categoria={categoria}
            onCategoria={setCategoria}
            onElegir={(p) => {
              setMarcado(null)
              elegir(p)
            }}
            filtro={codigo}
            marcado={marcado}
            onColumnas={setColumnas}
          />
        )}
      </section>

      {/* Derecha: ticket */}
      {/* 440 px y no 400: el renglón de cada línea dice el nombre, la
          cantidad, el precio unitario y el importe, y a 400 el nombre se
          cortaba con puntos suspensivos justo en los productos que se
          distinguen por el final («Manzana roja», «Manzana verde»). El monitor
          del mostrador lo lee el cliente de costado y a un metro. */}
      <aside className="flex min-h-0 w-full flex-col overflow-hidden rounded-xl border border-borde bg-papel lg:w-[440px]">
        <header className="flex items-center justify-between gap-2 border-b border-borde px-3 py-2.5">
          <span className="flex items-center gap-2 text-[14px] font-semibold text-tinta">
            <ShoppingCart className="h-4 w-4 text-tinta-tenue" />
            Ticket
            {lineas.length > 0 && (
              <span className="num rounded-full bg-crema-alt px-2 py-0.5 text-[12px] text-tinta-suave">
                {lineas.length}
              </span>
            )}
          </span>

          <span className="flex items-center gap-2">
            {/* Empezar de nuevo. Aparece solo con algo cargado, y pide
                confirmación: el cliente que cambia de idea a mitad del ticket
                es habitual, y hasta ahora la única salida era borrar renglón
                por renglón con la papelera de cada línea. */}
            {/* También con el turno cerrado: era la otra mitad del mismo bug.
                Lo que se cargó de taquito con la caja cerrada no tenía cómo
                sacarse, porque este botón se escondía justo ahí. */}
            {lineas.length > 0 && (
              <button
                type="button"
                onClick={async () => {
                  const si = await confirmar({
                    titulo: '¿Vaciar el ticket?',
                    detalle: `Se van a quitar los ${lineas.length} renglones que están cargados.`,
                    confirmar: 'Vaciar',
                    peligrosa: true,
                  })
                  if (si) limpiar()
                }}
                className="btn-fantasma btn-sm text-rojo"
              >
                <Trash2 className="h-4 w-4" />
                Vaciar
              </button>
            )}

            {usuario && (
              <span className="truncate text-[12.5px] text-tinta-tenue">{usuario.nombre}</span>
            )}
          </span>
        </header>

        {/* Lo que se acaba de cobrar, cuando el local no quiere el ticket en
            pantalla. Se muestra solo con el ticket vacío: en cuanto entra el
            primer renglón de la venta siguiente, el aviso ya no es lo que hay
            que mirar. El VUELTO va grande, porque es el único dato de esta
            operación que después no se puede consultar en ningún lado. */}
        {ultima && lineas.length === 0 && !sinTurno && (
          <div className="border-b border-verde/30 bg-verde-tenue px-3 py-2.5">
            <div className="flex items-baseline justify-between gap-2">
              <span className="flex items-center gap-1.5 text-[13px] font-medium text-verde-oscuro">
                <Check className="h-4 w-4" />
                Cobrado {plataCentavos(ultima.totalCentavos)}
              </span>

              <button
                type="button"
                onClick={() => setEmitida(ultima)}
                className="btn-fantasma btn-sm text-verde-oscuro"
              >
                Ver el ticket
              </button>
            </div>

            {ultima.vueltoCentavos != null && ultima.vueltoCentavos > 0 && (
              <p className="mt-1 flex items-baseline justify-between gap-2">
                <span className="text-[12.5px] font-medium uppercase tracking-wide text-verde-oscuro">
                  Vuelto
                </span>

                <span className="num text-[24px] font-bold leading-none text-verde-oscuro">
                  {plataCentavos(ultima.vueltoCentavos)}
                </span>
              </p>
            )}
          </div>
        )}

        {/* Con el turno cerrado y el ticket VACÍO, el cartel grande ocupa el
            panel: no hay nada más que mirar y lo único que se puede hacer es
            abrir el turno.

            Con renglones cargados, NO. El local lo reportó así: «no tengo el
            turno abierto y me deja cargar cosas, aunque no las muestra». La
            grilla seguía agregando al ticket —y está bien que lo haga, porque
            los renglones sobreviven a abrir el turno y no se pierde nada— pero
            el panel reemplazaba la lista por el cartel, así que el encabezado
            decía «1», el total decía $3.300 y no se veía un solo renglón. Un
            ticket que se traga lo que se carga es lo peor que puede hacer esta
            pantalla: el cajero no sabe si entró, si entró dos veces, ni qué
            entró. Ahora el cartel pasa a ser una banda arriba de los
            renglones. */}
        {sinTurno && lineas.length === 0 ? (
          <div className="flex flex-1 items-center justify-center">
            <CajaCerrada />
          </div>
        ) : (
          <>
            {sinTurno && <CajaCerrada compacta />}

            <Ticket
              lineas={lineas}
              seleccionada={seleccionada}
              onSeleccionar={seleccionar}
              onSumar={sumarUnidad}
              onQuitar={quitar}
              onEditarCantidad={(uid) => {
                const l = lineas.find((x) => x.uid === uid)
                if (l)
                  setPidiendoCantidad({ producto: l.producto, uid, inicial: l.cantidadMilesimas })
              }}
            />
          </>
        )}

        {/* Totales y cobro */}
        <footer className="border-t border-borde bg-crema-alt px-3 py-3">
          {totales.descuentoLineas > 0 && (
            <div className="mb-2 flex items-baseline justify-between text-[13px]">
              <span className="text-tinta-tenue">Ahorro en promociones</span>
              <span className="num font-medium text-verde-oscuro">
                −{plataCentavos(totales.descuentoLineas)}
              </span>
            </div>
          )}

          <div className="mb-3 flex items-baseline justify-between gap-2">
            <span className="text-[13px] font-medium uppercase tracking-wide text-tinta-tenue">
              Total
            </span>

            <span className="num text-[32px] font-bold leading-none text-tinta">
              {plataCentavos(totales.total)}
            </span>
          </div>

          <button
            type="button"
            onClick={() => void abrirCobro()}
            disabled={lineas.length === 0 || sinTurno || cotizando}
            className="btn-primario w-full py-4 text-[16px]"
          >
            {cotizando ? 'Calculando…' : (
              <>
                Cobrar <span className="num opacity-70">F2</span>
              </>
            )}
          </button>

          {/* Cobro sin detalle. Va DEBAJO del botón de cobrar y no arriba
              aunque en ese local sea la operación más frecuente: el de arriba
              es el que cierra la venta que está en pantalla, y cambiarlos de
              lugar haría que el cajero que sí cargó la mercadería registre un
              cobro sin detalle por costumbre.

              Solo aparece si el local lo habilitó: en una verdulería que carga
              todo, este botón es una forma de vaciar el reporte de ventas sin
              querer. */}
          {puedeCobroDirecto && (
            <button
              type="button"
              onClick={() => {
                setErrorCobro(null)
                setCobroDirecto(true)
              }}
              className="btn-secundario mt-2 flex w-full items-center justify-center gap-2 py-3 text-[14px]"
            >
              <Receipt className="h-4 w-4" />
              Cobrar sin detalle <span className="num opacity-70">F3</span>
            </button>
          )}
        </footer>
      </aside>

      {/* Diálogos */}
      {pidiendoCantidad && (
        <DialogoCantidad
          producto={pidiendoCantidad.producto}
          inicialMilesimas={pidiendoCantidad.inicial}
          balanza={capacidades.balanza ? (balanza.data ?? null) : null}
          onCerrar={() => setPidiendoCantidad(null)}
          onConfirmar={(r) => {
            if (pidiendoCantidad.uid && r.cantidadMilesimas !== undefined) {
              cambiarCantidad(pidiendoCantidad.uid, r.cantidadMilesimas)
            } else {
              agregar(pidiendoCantidad.producto, r)
            }

            setPidiendoCantidad(null)
          }}
        />
      )}

      {cobrando && cotizacion && (
        <DialogoCobro
          cotizacion={cotizacion}
          medios={medios.data ?? []}
          pagos={pagos}
          entregadoCentavos={entregadoCentavos}
          cliente={cliente}
          onFijarCliente={fijarCliente}
          onAgregarPago={agregarPago}
          onQuitarPago={quitarPago}
          onFijarEntregado={fijarEntregado}
          onCobrar={cobrar}
          onCerrar={() => {
            setCobrando(false)
            setCotizacion(null)
          }}
          cobrando={enviando}
          error={errorCobro}
        />
      )}

      {cobroDirecto && (
        <DialogoCobroDirecto
          medios={medios.data ?? []}
          onCobrar={(datos) => void registrarCobroDirecto(datos)}
          onCerrar={() => setCobroDirecto(false)}
          cobrando={enviando}
          error={errorCobro}
          // Ausente cuenta como SÍ: el parámetro se agregó después, y una caja
          // que todavía no sincronizó no puede quedarse sin el campo que hoy
          // está usando.
          pideComprobante={opciones.data?.cobroDirectoComprobante !== false}
        />
      )}

      {emitida && (
        <DialogoTicket
          venta={emitida}
          onReimprimir={reimprimir}
          onCerrar={() => setEmitida(null)}
        />
      )}
    </div>
  )
}

// ── Barra superior ──────────────────────────────────────────────────────────

function BarraSuperior({
  codigo,
  setCodigo,
  onEnter,
  refCodigo,
  conBalanza,
  balanzaConectada,
}: {
  codigo: string
  setCodigo: (v: string) => void
  onEnter: () => void
  refCodigo: React.RefObject<HTMLInputElement>
  conBalanza: boolean
  balanzaConectada: boolean
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* En qué caja está parada esta pestaña. Solo aparece en el navegador y
          solo si el local tiene más de una caja web (ver SelectorCaja). */}
      <SelectorCaja compacto />

      {/* El ÚNICO buscador de la pantalla.
          Eran dos, uno abajo del otro: este, para el lector y los códigos, y
          el de la grilla, para el nombre. Dos campos que hacen lo mismo
          obligan a elegir en cuál tipear antes de empezar —y el lector escribe
          en el que tenga el foco, así que escanear con el foco en el otro
          dejaba el código pegado en un filtro—. Acá entran las cuatro cosas:
          lo que dispara el lector, la etiqueta de la balanza, un PLU y el
          nombre del producto. Ver `procesarCodigo`. */}
      <div className="relative min-w-[240px] flex-1">
        <Barcode className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-tinta-tenue" />

        <input
          ref={refCodigo}
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onEnter()
            }
          }}
          placeholder="Código, PLU, etiqueta de balanza o nombre del producto…"
          // El lector de códigos es un teclado: escribe y manda Enter. Este
          // campo tiene que tener el foco casi siempre, y por eso la pantalla
          // se lo devuelve al cerrar cualquier diálogo.
          autoComplete="off"
          spellCheck={false}
          // Sin `num`: por acá ahora se escribe también el nombre del
          // producto, y la mono tabular está para las columnas de números.
          className="w-full rounded-xl border-2 border-verde/30 bg-papel py-3 pl-11 pr-11
                     text-[16px] outline-none focus:border-verde"
        />

        {codigo && (
          <button
            type="button"
            onClick={() => setCodigo('')}
            aria-label="Limpiar la búsqueda"
            className="absolute right-2.5 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center
                       rounded-lg text-tinta-tenue hover:bg-crema-alt"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {conBalanza && (
        <span
          className={clsx(
            'flex items-center gap-1.5 rounded-full px-3 py-2 text-[12.5px] font-medium',
            balanzaConectada ? 'bg-verde-tenue text-verde-oscuro' : 'bg-crema-alt text-tinta-tenue',
          )}
          title={
            balanzaConectada
              ? 'La balanza está conectada'
              : 'La balanza no responde: se puede tipear el peso a mano'
          }
        >
          <Scale className="h-3.5 w-3.5" />
          {balanzaConectada ? 'Balanza' : 'Sin balanza'}
        </span>
      )}
    </div>
  )
}
